import React, {useState,useRef, Dispatch, forwardRef, SetStateAction, useCallback, useEffect, useImperativeHandle } from 'react';
import i18n from 'i18n';
import { useTranslation } from 'react-i18next';
import { Button, Icon, Label, RadioButton, SelectField, Switch, TextField, useCopyToClipboard, useNotification } from 'scorer-ui-kit';
import styled, { css } from 'styled-components';
import { addCamera, getDeviceInfo, getFTPServerSetting, updateCamera } from 'services/apiConfig';
import { useHistory } from 'react-router-dom';
import AuthService from 'services/authService';
import { DIR_TYPE, FILE_EXT, FTP_IP_URL, FTP_MDNS_URL, FTP_EXAMPLE_URL, DateTimeFormatInfo } from '../../constants';
import UsePasswordField from 'hooks/usePassword';

const Container = styled.div`
  margin: 0;
  padding-top: 28px;
  user-select: none;
`;

const InputFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  top: 30px;
  max-width: 840px;
`;

const ServerBox = styled.div`
  position: relative;
`;

const CopyIcon = styled.div<{ja:boolean}>`
  position: absolute;
  top: 40px;
  right: 13px;
  cursor: pointer;
  width: 20px;
  height: 21px;

  &:active, &:focus {
    outline: none;
    &:after {
      ${({ ja }) => ja ? css` content :'コピー品です';` : css` content : 'Copied!';`}
      background: rgba(0,0,0,.72);
      color: #fff;
      font-size: 12px;
      padding: 5px;
      white-space: nowrap;
      top: 24px;
      left: 28px;
      position: absolute;
    }
  }
`;

const IPDNSbutton = styled.div`
  position: absolute;
  display:flex;
  right: 0;
  button{
    margin-left: 10px;
  }
`;

const TextFieldRequired = styled.div<{ required?: boolean }>`
  ${({ required }) => required && css`
      span::after{
      font-weight: bold;
      content: " *";
      color: rgb(238, 75, 43);
    }
  `}
`;

const VideoConfigBox = styled.div`
  margin: 0;
`;

const BaseConfig = styled.div`
  margin: 30px 0;
  max-width: 1020px;
`;

const TextConfigBox = styled.div`
  position: relative;
`;

const TextConfig = styled(Label)`
  position: absolute;
  font-size: 16px;
  color: #5a6269;
  background: #f9fafb;
  z-index: 1;
  padding-right: 18px;
`;

const Line = styled.div`
  position: absolute;
  height: 1px;
  top: 12px;
  width: 100%;
  background-color: #eee;
`;

const RadioWrapper = styled.div`
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 30px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: -15px;
  padding-top: 40px;
`;

const RadioContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 30px;
  margin-top: -30px;
`;

interface SwitchContainerProps {
  paddingTop?: boolean;
}

const SwitchContainer = styled.div<SwitchContainerProps>`
  display: flex;
  flex-direction: row;
  margin-top: 20px;
  padding-top: ${({ paddingTop }) => paddingTop ? '0px' : '30px'};
  gap: 20px;
`;

const LoginDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const InputFieldBox = styled.div`
  max-width: 378px;
`;

const PasswordContainer = styled.div`
  button > div {
    margin-top: 5px;
  }
`;

const SwitchText = styled(Label)`
  line-height: 22px;
  text-align: left;
  font-size: 14px;
  margin-bottom: 0;
  span{
    margin-bottom: 0;
  }
`;

const DateTimeFormatContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const Text = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: hsl(207,5%,57%);
  /* margin-bottom: 20px; */
`;

const TextFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 250px;
`;

const InfoIcon = styled.div`
  margin-left: -120px;
  margin-top: -4px;
  width: 15px;
  height: 15px;
  cursor: pointer;
  font-size: 20px;
  position: relative;

  &:hover + div {
    display: block;
    padding: 10px;
  }
`;

const TooltipText = styled.p`
  margin: 0;
  padding-right: 20px;
`;

const Tooltip = styled.div`
  display: none;
  position: absolute;
  left: 55%;
  top: 40%;
  background-color: #fff;
  border: 1px solid #ccc;
  padding: 10px;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  z-index: 10;
  white-space: nowrap;

  ${InfoIcon}:hover & {
    display: block;
  }
`;

interface IBasicConfig {
  camera_name: string;
  camera_type: string;
  camera_enabled: boolean;
}

interface IFTPCameraData {
  file_extension: string;
  source_directory: string;
  source_directory_type: string;
  source_url: string;
  url_type: string;
  // destinations: string[]; 
  enable_anonymous_access: boolean; 
  enable_user_login: boolean; 
  ingest_time: string; 
  date_time_format: string;
  username: string; 
  password: string;
}

interface IFTPCamera {
  basicConfig: IBasicConfig;
  ftpCamera: IFTPCameraData;
  isEditCamera: boolean;
  streamName: string;
  setFtpCamera: Dispatch<SetStateAction<IFTPCameraData>>;
  setEditButtonLoading:(data:boolean)=>void;
  setCompareNewValues: (data:any)=>void;
  mainFetchAllCamera: () => Promise<void>;
}

interface IInterfaces {
  interface: string,
  ip_address: string
}


const FTPCamera = forwardRef<any, IFTPCamera>(({ basicConfig, ftpCamera, setFtpCamera, isEditCamera, streamName, setEditButtonLoading, setCompareNewValues, mainFetchAllCamera }, ref) => {

  const [deviceInfo, setDeviceInfo] = useState({'device_id': '', 'device_ip': ''});
  const { t } = useTranslation(['CommonDict']);
  const { copyToClipboard } = useCopyToClipboard();
  const { sendNotification } = useNotification();
  const notificationRef = useRef(sendNotification);
  const { push } = useHistory();

  const fetchFTPServerSetting = useCallback(async () => {
    try {
      const res = await getFTPServerSetting();
      if (res.data) {
        setFtpCamera(prev => ({ ...prev, password: res.data.data.password }));
      }
    } catch (error) {
      console.error((error as Error).message);
    }
  }, [setFtpCamera]);

  useEffect(() => {
    fetchFTPServerSetting();
  }, [fetchFTPServerSetting]);


  useEffect(() => {
    const fetchDeviceInfo = async () => {
      try {
        const { data: { data, status_code } } = await getDeviceInfo();
        if (status_code === '10000') {
          let deviceIP = '';
          const interfaces = data.network.interfaces;
          Object.entries(interfaces).map((arr)=>{
            const infData = (arr[1] as IInterfaces);
            if(infData.interface === 'eno1.inet'){
              deviceIP = infData.ip_address.split('/')[0];
            }
            if(deviceIP === '' && infData.interface === 'eth0.inet'){
              deviceIP = infData.ip_address.split('/')[0];
            }
            if(deviceIP === '' && infData.interface === 'wlan0.inet'){
              deviceIP = infData.ip_address.split('/')[0];
            }
            return arr;
          });
          setDeviceInfo({'device_id': data?.device_id, 'device_ip': deviceIP});
        } else {
          notificationRef.current({ type: 'success', message: t('Failed to load device info') });
        }
      } catch (error) {
        console.error((error as Error).message);
        notificationRef.current({ type: 'error', message: t('Failed to communicate with the system') });
      }
    };
    fetchDeviceInfo();
  }, [t]);


  useImperativeHandle(ref, () => ({
    postData: async() => {
      const data = { ...basicConfig,  camera_name: basicConfig.camera_name.trim(), ...ftpCamera };
      if (ftpCamera?.source_url==='') {
        notificationRef.current({
          type: 'error', message: t('Please enter server URL.')
        });
        return;
      }
      try {
        if(isEditCamera){          
          setEditButtonLoading(true);
          const { data: { status_code } } = await updateCamera({ ...data, stream_name: streamName });
          if (status_code === 201) {
            mainFetchAllCamera();
            notificationRef.current({ type: 'success', message: t('Camera details updated successfully') });
            push('/cameras');
          }
          else if (status_code === 403) {
            notificationRef.current({ type: 'error', message: t('Authorization required to access') });
            if (await AuthService.logoutUser() !== 200) {
              notificationRef.current({ type: 'error', message: t('Failed to communicate with the system') });
            }
          }
          else {
            notificationRef.current({ type: 'error', message: t('Failed to add camera') });
          }
        } else {
          setEditButtonLoading(true);
          const { data: { status_code } } = await addCamera(data);
          if (status_code === 201) {
            mainFetchAllCamera();
            notificationRef.current({ type: 'success', message: t('Camera added successfully') });
            push('/cameras');
          }
          else if (status_code === 403) {
            notificationRef.current({ type: 'error', message: t('Authorization required to access') });
            if (await AuthService.logoutUser() !== 200) {
              notificationRef.current({ type: 'error', message: t('Failed to communicate with the system') });
            }
          }
          else {
            notificationRef.current({ type: 'error', message: t('Failed to add camera') });
          }
        }
        setEditButtonLoading(false);
      } catch (error) {
        console.error((error as Error).message);
        notificationRef.current({ type: 'error', message: t('Failed to communicate with the system') });
      }
      setEditButtonLoading(true);
    }
  }));

  useEffect(()=>{
    const data = { ...basicConfig,  camera_name: basicConfig.camera_name.trim(), ...ftpCamera };
    setCompareNewValues(data);
  }, [basicConfig, ftpCamera, setCompareNewValues]);


  useEffect(() => {
    setFtpCamera(prev => ({ 
      ...prev, 
      source_url: ftpCamera.url_type === 'IP' ? FTP_IP_URL.replace('{X}', deviceInfo.device_ip) : FTP_MDNS_URL.replace('{X}', deviceInfo.device_id),
      url_type: ftpCamera.url_type === 'IP' ? 'IP' : 'mDNS',
    }));
  }, [basicConfig.camera_name, deviceInfo, ftpCamera.url_type, setFtpCamera]);


  const onChangeHandler = useCallback(({ target: { name, value } }) => {
    if(name === 'source_directory_type') {
      if(value === 'default_source_directory') {
        setFtpCamera((prev) => ({ ...prev, source_directory: basicConfig.camera_name, source_directory_type: value }));
      } else if(value === 'custom_source_directory') {
        setFtpCamera((prev) => ({ ...prev, source_directory: '', source_directory_type: value }));
      }
    } else if(name === 'date_time_format') {
      setFtpCamera((prev) => ({ ...prev, date_time_format: value }));
    } else {
      setFtpCamera((prev) => ({ ...prev, [name]: value }));        
    }
  }, [basicConfig.camera_name, setFtpCamera]);
  

  const onClickIPDNS = useCallback((type: string) => {    
    setFtpCamera((prev) => ({ ...prev, url_type: type }));
  }, [setFtpCamera]);


  const handleChangeRadioButton = (value: any) => {
    setFtpCamera(prev => ({ ...prev, ingest_time: value }));
  };


  return (
    <Container>
      <InputFieldContainer>
        
        <InputFieldBox>
          <ServerBox>

            <IPDNSbutton>
              <Button onClick={() => onClickIPDNS('IP')} design={ftpCamera.url_type === 'IP' ? 'primary' : 'secondary'} size='xsmall'>{t('IP')}</Button>
              <Button onClick={() => onClickIPDNS('mDNS')} design={ftpCamera.url_type === 'IP' ? 'secondary' : 'primary'} size='xsmall'>{t('mDNS')}</Button>
            </IPDNSbutton>
            <TextFieldRequired>
              <TextField
                tabIndex={4}
                name='source_url'
                label={t('Server URL')}
                fieldState='disabled'
                onChange={onChangeHandler}
                value={ftpCamera.source_url}
                maxLength={255}
                placeholder={FTP_EXAMPLE_URL}
              />
            </TextFieldRequired>

            <CopyIcon ja={i18n.language === 'ja'} title={t('Copy')} onClick={() => { copyToClipboard(ftpCamera.source_url);}}>
              <Icon size={20} icon='Copy' color='dimmed' />
            </CopyIcon>
          </ServerBox>
          

          <TextFieldRequired>
            <SelectField
              name='source_directory_type'
              tabIndex={5}
              changeCallback={(value: string | number) => onChangeHandler({ target: { name: 'source_directory_type', value } })}
              value={ftpCamera.source_directory_type}
              label={{
                htmlFor: 'Directory To Fetch Images',
                text: t('Directory To Fetch Images')
              }}
            >
              {DIR_TYPE.map(item => (<option key={item.name} value={item.value}>{t(item.name)}</option>))}
            </SelectField>
          </TextFieldRequired>

          <TextField
            tabIndex={6}
            name='source_directory'
            label=''
            fieldState={ftpCamera.source_directory_type === 'custom_source_directory' ? 'default' : 'disabled'}
            onChange={onChangeHandler}
            value={ftpCamera.source_directory_type === 'default_source_directory' ? basicConfig.camera_name : ftpCamera.source_directory}
            maxLength={255}
          />

          <SelectField
            tabIndex={7}
            changeCallback={(event: string | number) => setFtpCamera((prev: any) => ({ ...prev, file_extension: event as string }))}
            value={ftpCamera.file_extension}
            label={{
              htmlFor: 'File Extension',
              text: t('File Extension')
            }}
          >
            {FILE_EXT.map(item => (<option key={item.name} value={item.value}>{t(item.name)}</option>))}
          </SelectField>

        </InputFieldBox>

        <VideoConfigBox>
          <BaseConfig>
            <TextConfigBox>
              <TextConfig htmlFor='' labelText={t('Ingest Time')} />
              <Line />
            </TextConfigBox>

            <Wrapper>
              <Label htmlFor='' labelText={t('Based On')} />
              <RadioContainer>
                <RadioWrapper>
                  <Label
                    htmlFor='systemClock'
                    labelText={t('System Clock')}
                    rightAlign
                  >
                    <RadioButton
                      currentChecked={ftpCamera.ingest_time}
                      id='systemClock'
                      name='systemClock'
                      onChangeCallback={handleChangeRadioButton}
                      value='System Clock'
                    />
                  </Label>
                  <Label
                    htmlFor='fileName'
                    labelText={t('File Name')}
                    rightAlign
                  >
                    <RadioButton
                      currentChecked={ftpCamera.ingest_time}
                      id='fileName'
                      name='fileName'
                      onChangeCallback={handleChangeRadioButton}
                      value='File Name'
                    />
                  </Label>
                </RadioWrapper>
                  
                {ftpCamera.ingest_time === 'File Name' && (
                  <DateTimeFormatContainer>
                    <TextFieldContainer>
                      <Text>{t('Date Time Format')}</Text>
                      <TextField
                        tabIndex={8}
                        name='date_time_format'
                        fieldState='default'
                        label=''
                        onChange={onChangeHandler}
                        value={ftpCamera.date_time_format}
                        maxLength={255}
                      />
                    </TextFieldContainer>
                    <InfoIcon>
                      <Icon
                        color='primary'
                        icon='Information'
                        size={16}
                        weight='regular'
                      />
                    </InfoIcon>
                    <Tooltip>
                      <ul>
                        <TooltipText>{t('For example,')}</TooltipText>
                        {DateTimeFormatInfo.map((format: string, index: number) => (
                          <li key={index}>{format}</li>                        
                        ))}
                      </ul>
                    </Tooltip>
                  </DateTimeFormatContainer>
                )}
              </RadioContainer>
            </Wrapper>
          </BaseConfig>

          <BaseConfig>
            <TextConfigBox>
              <TextConfig htmlFor='' labelText={t('Login Details')} />
              <Line />
            </TextConfigBox>

            <LoginDetailsContainer>
              <SwitchContainer>
                <Label htmlFor='' labelText={t('Enable User Login')} />
                <Switch
                  checked={ftpCamera.enable_user_login}
                  leftTheme='off'
                  onChangeCallback={() => setFtpCamera(prev => ({ ...prev, enable_user_login: !prev.enable_user_login }))}
                  rightTheme='on'
                  state='default'
                />
                <SwitchText htmlFor='' labelText={ftpCamera.enable_user_login ? t('Enabled') : t('Disabled')} />
              </SwitchContainer>

              <InputFieldBox>
                <input type='username' style={{'display': 'none'}} />
                <TextField
                  tabIndex={3}
                  name='username'
                  label={t('User Name')}
                  fieldState='default'
                  onChange={onChangeHandler}
                  value={ftpCamera.username}
                  maxLength={255}
                  autoComplete='off'
                  width='378px'
                />
                <PasswordContainer>
                  <input type='password' style={{'display': 'none'}} />
                  <UsePasswordField
                    name='password'
                    value={ftpCamera.password}
                    type='text'
                    setInput={onChangeHandler}
                    autoComplete='off'
                    width='378px'
                    height='40px'
                    label={t('Password')}
                  />
                </PasswordContainer>                
              </InputFieldBox>

              <SwitchContainer paddingTop>
                <Label htmlFor='' labelText={t('Allow Anonymous Access')} />
                <Switch
                  checked={ftpCamera.enable_anonymous_access}
                  leftTheme='off'
                  onChangeCallback={() => setFtpCamera(prev => ({ ...prev, enable_anonymous_access: !prev.enable_anonymous_access }))}
                  rightTheme='on'
                  state='default'
                />
                <SwitchText htmlFor='' labelText={ftpCamera.enable_anonymous_access ? t('Enabled') : t('Disabled')} />
              </SwitchContainer>
            </LoginDetailsContainer>
          
          </BaseConfig>
        </VideoConfigBox>
      </InputFieldContainer>
    </Container>
  );
});

export default FTPCamera;